var m = require('mithril');
var moment = require('moment-timezone');
var prop = require('mithril/stream');
var R = require('ramda');
var loaders = require('../layout/components/loaders');
var io = require('../libs/socketio');
var dragscroll = require('../libs/dragscroll');
var { Promise } = require('bluebird');
var alarmsTablePagination = require('./alarmsTablePagination');
var alarmState = require('./alarmState');
var heading = require('../components/heading');
var commentModal = require('../data/components/commentAlarmModal');
var session = require('../auth/models/session');

var dateFormat = 'MM-DD-YYYY hh:mm:ss a';
var trippedOrPendingReset = R.flip(R.contains)(['Alarm Tripped', 'Pending Alarm Reset']); //bool

var isActive = R.propSatisfies(trippedOrPendingReset, 'alarmStatus'); //bool
var isInactive = R.complement(isActive);
var sortDateAscending = R.sort((a, b) => a.lastTripTimestamp - b.lastTripTimestamp);

var alarmsTable = {
  oninit: function (vnode) {
    var ctrl = this;
    // Accept missing/optional attrs by providing safe defaults
    ctrl.location = vnode.attrs.location;
    ctrl.equipmentAllRids = prop(vnode.attrs.equipmentAllRids);
    ctrl.dashboardsAllRids = prop(vnode.attrs.dashboardsAllRids);
    ctrl.isLoading = prop(true);
    ctrl.alarmsRids = prop([]);
    ctrl.alarmRecords = prop([]);
    ctrl.alarmRecordsToShow = prop([]);
    ctrl.trippedOrPendingResetAlarms = prop([]);
    ctrl.inactiveAlarms = prop([]);

    ctrl.trippedAlarms = prop([]);
    ctrl.pendingResetAlarms = prop([]);
    ctrl.pendingTripAlarms = prop([]);
    ctrl.clearAlarms = prop([]);

    ctrl.sortByLastActive = prop(true);
    ctrl.sortByStatus = prop(false);
    ctrl.updateTimer = null;
    ctrl.subscribedGroups = new Set();
    ctrl.tableBodyEl = null;
    ctrl.alarmsVersion = prop(0);
    ctrl.alarmSnapshot = new Map();
    ctrl.highlightRids = new Set();
    ctrl.pendingPositions = null;
    ctrl.isUpdatesPaused = prop(false);
    ctrl.resumeTimer = null;
    ctrl.documentClickHandler = null;

    // Default to a no-op loader flag when not provided
    ctrl.equipmentTableLoading = prop(vnode.attrs.equipmentTableLoading || false);

    ctrl.captureRowPositions = function () {
      if (!ctrl.tableBodyEl) return null;
      const rows = ctrl.tableBodyEl.querySelectorAll('tr[data-alarm-row="true"]');
      if (!rows || !rows.length) return null;
      const positions = new Map();
      rows.forEach((row) => {
        const rid = row.getAttribute('data-rid');
        if (!rid) return;
        positions.set(rid, row.getBoundingClientRect());
      });
      return positions;
    };

    ctrl.flashRow = function (row) {
      row.classList.add('alarms-table-row-new');
      setTimeout(function () {
        row.classList.remove('alarms-table-row-new');
      }, 2600);
    };

    ctrl.animateRowPositions = function (prevPositions) {
      if (!prevPositions || !ctrl.tableBodyEl) return;
      const rows = ctrl.tableBodyEl.querySelectorAll('tr[data-alarm-row="true"]');
      if (!rows || !rows.length) return;
      rows.forEach((row) => {
        const rid = row.getAttribute('data-rid');
        const prev = prevPositions.get(rid);
        const next = row.getBoundingClientRect();
        if (!prev) {
          row.classList.add('alarms-table-row-enter');
          ctrl.flashRow(row);
          row.addEventListener('animationend', function () {
            row.classList.remove('alarms-table-row-enter');
          }, { once: true });
          return;
        }
        if (ctrl.highlightRids && ctrl.highlightRids.has(rid)) {
          ctrl.flashRow(row);
        }
        const deltaY = prev.top - next.top;
        if (!deltaY) return;
        row.style.transform = `translateY(${deltaY}px)`;
        row.style.transition = 'transform 0s';
        row.getBoundingClientRect();
        row.style.transition = '';
        row.style.transform = '';
      });
    };

    ctrl.init = function (options) {
      const opts = options || {};
      const allowSilent = !!(opts.silent && ctrl.alarmRecords().length > 0);
      if (!allowSilent) {
        ctrl.isLoading(true);
      }
      if (ctrl.equipmentTableLoading && ctrl.equipmentTableLoading()) {
        return;
      }
      
      const prevPositions = allowSilent ? ctrl.captureRowPositions() : null;
      ctrl.pendingPositions = prevPositions;
      if (!allowSilent) {
        ctrl.alarmRecordsToShow([]);
        ctrl.alarmsRids([]);
      }
      
      return Promise.all([
        ctrl.dashboardsAllRids().length > 0 ?
          loaders.requestWithLoader({
            loader: 'background',
            url: `/api/v1/dashboard/alarms`,
            method: 'POST',
            body: {
              rids: ctrl.dashboardsAllRids()
            }
          })
          : Promise.resolve([]),
        ctrl.equipmentAllRids().length > 0 ?
          loaders.requestWithLoader({
            loader: 'background',
            url: `/api/v1/equipmentalarms`,
            method: 'POST',
            body: {
              rids: ctrl.equipmentAllRids().map(r => r['@rid'])
            }
          })
          : Promise.resolve([])
      ])
        .then(response => {
          const all = [...response[0], ...response[1]];
          ctrl.trippedOrPendingResetAlarms(
            sortDateAscending(R.filter(isActive, all)).map(r => r['@rid'])
          );
          ctrl.inactiveAlarms(
            sortDateAscending(R.filter(isInactive, all)).map(r => r['@rid'])
          );
          ctrl.alarmRecords(all.sort((a, b) => {
            const timeA = a.lastTripTimestamp ? a.lastTripTimestamp : -Infinity;
            const timeB = b.lastTripTimestamp ? b.lastTripTimestamp : -Infinity;
            return timeB - timeA;
          }));

          ctrl.trippedAlarms(
            sortDateAscending(R.filter(R.pipe(R.propOr('', 'alarmStatus'), R.defaultTo(''), R.toLower, R.includes('alarm tripped')), all)).map(r => r['@rid'])
          );

          ctrl.pendingResetAlarms(
            sortDateAscending(R.filter(R.pipe(R.propOr('', 'alarmStatus'), R.defaultTo(''), R.toLower, R.includes('pending alarm reset')), all)).map(r => r['@rid'])
          );

          ctrl.pendingTripAlarms(
            sortDateAscending(R.filter(R.pipe(R.propOr('', 'alarmStatus'), R.defaultTo(''), R.toLower, R.includes('pending alarm trip')), all)).map(r => r['@rid'])
          );

          ctrl.clearAlarms(
            sortDateAscending(R.filter(R.pipe(R.propOr('', 'alarmStatus'), R.defaultTo(''), R.toLower, R.includes('clear')), all)).map(r => r['@rid'])
          );

          ctrl.alarmsRids(ctrl.alarmRecords().map(r => r['@rid']));
          const nextSnapshot = new Map();
          const nextHighlights = new Set();
          all.forEach((record) => {
            if (!record || !record['@rid']) return;
            const rid = record['@rid'];
            const prevRecord = ctrl.alarmSnapshot.get(rid);
            nextSnapshot.set(rid, {
              lastTripTimestamp: record.lastTripTimestamp,
              alarmStatus: record.alarmStatus
            });
            if (allowSilent && (!prevRecord ||
              prevRecord.lastTripTimestamp !== record.lastTripTimestamp ||
              prevRecord.alarmStatus !== record.alarmStatus)) {
              nextHighlights.add(rid);
            }
          });
          ctrl.alarmSnapshot = nextSnapshot;
          ctrl.highlightRids = nextHighlights;
          ctrl.alarmsVersion(ctrl.alarmsVersion() + 1);
        })
        .finally(() => {
          if (!allowSilent) {
            ctrl.isLoading(false);
          }
          m.redraw();
        })
    }

    // Trigger initial load
    ctrl.init();

    ctrl.ensureSubscribedToGroups = function () {
      const user = session.user && session.user();
      const groups = user && user.groups ? user.groups : [];
      if (!Array.isArray(groups)) return;
      groups.forEach((g) => {
        if (!g || !g['@rid'] || ctrl.subscribedGroups.has(g['@rid'])) return;
        io.emit('subscribeAlarmGroup', g['@rid']);
        ctrl.subscribedGroups.add(g['@rid']);
      });
    };

    ctrl.scheduleRefresh = function () {
      if (ctrl.updateTimer) return;
      if (ctrl.isUpdatesPaused && ctrl.isUpdatesPaused()) return;
      ctrl.updateTimer = setTimeout(function () {
        ctrl.updateTimer = null;
        ctrl.init({ silent: true });
      }, 1000);
    };

    ctrl.pauseUpdates = function () {
      if (ctrl.updateTimer) {
        clearTimeout(ctrl.updateTimer);
        ctrl.updateTimer = null;
      }
      ctrl.isUpdatesPaused(true);
      if (ctrl.resumeTimer) {
        clearTimeout(ctrl.resumeTimer);
      }
      ctrl.resumeTimer = setTimeout(function () {
        ctrl.resumeUpdates();
      }, 20000);
    };

    ctrl.resumeUpdates = function () {
      if (!ctrl.isUpdatesPaused()) return;
      ctrl.isUpdatesPaused(false);
      if (ctrl.resumeTimer) {
        clearTimeout(ctrl.resumeTimer);
        ctrl.resumeTimer = null;
      }
      ctrl.init({ silent: true });
    };

    ctrl.documentClickHandler = function (event) {
      if (!ctrl.isUpdatesPaused()) return;
      const target = event && event.target;
      if (!target || !target.closest) return;
      if (target.closest('[data-alarm-control="true"]')) return;
      ctrl.resumeUpdates();
    };

    document.addEventListener('click', ctrl.documentClickHandler);

    ctrl.handleRecordsUpdated = function () {
      if (!ctrl.pendingPositions) return;
      const prevPositions = ctrl.pendingPositions;
      ctrl.pendingPositions = null;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          ctrl.animateRowPositions(prevPositions);
        });
      });
    };

    ctrl.sortRecordsByLastActivation = function () {
      if (ctrl.sortByLastActive()) {
        ctrl.alarmsRids([...ctrl.alarmRecords()].sort((a, b) => {
          const timeA = a.lastTripTimestamp ? a.lastTripTimestamp : -Infinity;
          const timeB = b.lastTripTimestamp ? b.lastTripTimestamp : -Infinity;
          return timeB - timeA;
        }).map(r => r['@rid']));
      } else {
        ctrl.alarmsRids([...ctrl.alarmRecords()].sort((a, b) => {
          const timeA = a.lastTripTimestamp ? a.lastTripTimestamp : -Infinity;
          const timeB = b.lastTripTimestamp ? b.lastTripTimestamp : -Infinity;
          return timeA - timeB;
        }).map(r => r['@rid']));
      }
    }

    ctrl.sortRecordsByByStatus = function () {
      if (ctrl.sortByStatus()) {
        ctrl.alarmsRids([...ctrl.trippedAlarms(), ...ctrl.pendingResetAlarms(), ...ctrl.pendingTripAlarms(), ...ctrl.clearAlarms()]);
      } else {
        ctrl.alarmsRids([...ctrl.clearAlarms(), ...ctrl.pendingTripAlarms(), ...ctrl.pendingResetAlarms(), ...ctrl.trippedAlarms()]);
      }
    }

    ctrl.commentSocketHandler = function (payload) {
      if (!payload || !payload.elementRid || payload.elementRid !== 'alarms-table') {
        return;
      }
      ctrl.scheduleRefresh();
    };

    ctrl.alarmSocketHandler = function () {
      ctrl.scheduleRefresh();
    };

    ctrl.ensureSubscribedToGroups();
    io.on('comment-event', ctrl.commentSocketHandler);
    io.on('alarm-update', ctrl.alarmSocketHandler);
  },
  onupdate: function (vnode) {
    var ctrl = this;
    ctrl.ensureSubscribedToGroups && ctrl.ensureSubscribedToGroups();
    if (ctrl.location()['@rid'] != vnode.attrs.location()['@rid']) {
      ctrl.equipmentAllRids = prop(vnode.attrs.equipmentAllRids);
      ctrl.dashboardsAllRids = prop(vnode.attrs.dashboardsAllRids);
      ctrl.location = vnode.attrs.location;
      ctrl.init();
    }
    if (ctrl.equipmentAllRids().length != vnode.attrs.equipmentAllRids.length || ctrl.dashboardsAllRids().length != vnode.attrs.dashboardsAllRids.length) {
      ctrl.equipmentAllRids = prop(vnode.attrs.equipmentAllRids);
      ctrl.dashboardsAllRids = prop(vnode.attrs.dashboardsAllRids);
      ctrl.init();
    }

    if (vnode.attrs.equipmentTableLoading && (ctrl.equipmentTableLoading() != vnode.attrs.equipmentTableLoading())) {
      ctrl.equipmentTableLoading(vnode.attrs.equipmentTableLoading());
      ctrl.init();
    }
  },
  onremove: function () {
    if (this.commentSocketHandler) {
      io.off('comment-event', this.commentSocketHandler);
    }
    if (this.alarmSocketHandler) {
      io.off('alarm-update', this.alarmSocketHandler);
    }
    if (this.updateTimer) {
      clearTimeout(this.updateTimer);
      this.updateTimer = null;
    }
    if (this.resumeTimer) {
      clearTimeout(this.resumeTimer);
      this.resumeTimer = null;
    }
    if (this.documentClickHandler) {
      document.removeEventListener('click', this.documentClickHandler);
    }
  },
  view: function (vnode) {
    var ctrl = this;
    return m('div', [
      m(heading, {
        label: 'Alarms',
        labelClass: 'red',
        class: 'f2 mt3 items-center flex-wrap mb1 ',
      }, [
        m('div.flex.items-center.flex-wrap', [
          m('label.mr2.f5.moon-gray', 'Active:'),
          ctrl.isLoading() ?
            m('div.br2.nowrap.mv1.mr2', { style: { 'background-color': '#262626' } }, [
              m('label.f4.ph1.moon-gray', '—')
            ])
          :
            m('div.br2.nowrap.mv1.mr2', { style: { 'background-color': '#262626' } }, [
              m('label.f4.ph1.red', `${ctrl.trippedOrPendingResetAlarms().length}`)
            ]),
          m('label.mr2.f5.moon-gray', 'Total:'),
          ctrl.isLoading() ?
            m('div.br2.nowrap.mv1.mr2', { style: { 'background-color': '#262626' } }, [
              m('label.f4.ph1.moon-gray', '—')
            ])
          :
            m('div.br2.nowrap.mv1', { style: { 'background-color': '#262626' } }, [
              m('label.f4.ph1.moon-gray', `${ctrl.alarmRecords().length}`)
            ])
        ])
      ]),
      m('div.overflow-hidden.mb3', [
        m('div.dragscroll-container', [
          m('div.dragscroll.pb3.pr2', { oncreate: dragscroll }, [
            m('table.f4.pb3.narrow.w-100', [
              m('tbody', {
                oncreate: (vnode) => { ctrl.tableBodyEl = vnode.dom; },
                onupdate: (vnode) => { ctrl.tableBodyEl = vnode.dom; }
              }, [
                m('tr', [
                  m('td.bb.moon-gray.tl.nowrap.pr3.overflow-hidden.nowrap', /*'Last Activation'*/[
                    m('div', {
                      'data-alarm-control': 'true',
                      onclick: () => {
                        ctrl.pauseUpdates();
                        ctrl.sortByLastActive(!ctrl.sortByLastActive());
                        ctrl.sortRecordsByLastActivation();
                      }
                    }, [
                      'Last Activation',
                      ctrl.sortByLastActive() ? m('i.ion-ios-arrow-up.ml2.pointer.blue') : m('i.ion-ios-arrow-down.ml2.pointer.blue')
                    ])
                  ]),
                  m('td.bb.moon-gray.tl.nowrap.pr3.overflow-hidden', 'Name'),
                  m('td.bb.moon-gray.tl.nowrap.pr3.overflow-hidden', [
                    m('div', {
                      'data-alarm-control': 'true',
                      onclick: () => {
                        ctrl.pauseUpdates();
                        ctrl.sortByStatus(!ctrl.sortByStatus());
                        ctrl.sortRecordsByByStatus();
                      }
                    }, [
                      'Present Status',
                      ctrl.sortByStatus() ? m('i.ion-ios-arrow-down.ml2.pointer.blue') : m('i.ion-ios-arrow-up.ml2.pointer.blue')
                    ])
                  ]),
                  m('td.bb.moon-gray.tl.nowrap.pr3.overflow-hidden', 'Acknowledged'),
                  m('td.bb.moon-gray.tl.nowrap.pr3.overflow-hidden', 'Acknowledged By'),
                  m('td.bb.moon-gray.tl.nowrap.pr3.overflow-hidden', 'Comments'),
                ]),
                ctrl.isLoading() ?
                  (function() {
                    const rows = 10;
                    const makeBar = (w) => m('div.f4.br2.bg-dark-gray.o-20', { style: { height: '1em', width: w + '%' } });
                    const makeCell = (w) => m('td.bb.bl-0.pr2.skeleton', [ makeBar(w) ]);
                    const firstCell = (w) => m('td.bb.bl-0.pr2.skeleton', [ makeBar(w) ]);
                    const bodyRows = [];
                    for (let r = 0; r < rows; r++) {
                      const w1 = 50 + (r % 3) * 10; // 50,60,70
                    const w2 = 60 + ((r + 1) % 3) * 10; // 60,70,80
                    const w3 = 40 + ((r + 2) % 3) * 10; // 40,50,60
                    const w4 = 30 + ((r + 1) % 4) * 10; // 40..60
                    const w5 = 30 + ((r + 2) % 4) * 10; // 50..70
                    const w6 = 40 + ((r + 3) % 4) * 10; // 40..70
                    bodyRows.push(
                      m('tr', [
                        firstCell(w1),
                        makeCell(w2),
                        makeCell(w3),
                        makeCell(w4),
                        makeCell(w5),
                        makeCell(w6)
                      ])
                    );
                    }
                    return bodyRows;
                  })()
                :
                ctrl.alarmRecordsToShow().map((alarmRecord, index) => {
                  if (Object.keys(alarmRecord).length > 0) {
                    const latestIncident = alarmRecord.latestIncident || alarmRecord.lastIncident;
                    const commentsCount = (latestIncident && Array.isArray(latestIncident.out_SubjectOf)) ? latestIncident.out_SubjectOf.length : 0;

                    return m('tr', {
                      key: alarmRecord['@rid'],
                      class: 'alarms-table-row',
                      'data-alarm-row': 'true',
                      'data-rid': alarmRecord['@rid']
                    }, [
                      m('td.bb.bl-0.pr2.truncate', alarmRecord.lastTripTimestamp ? moment(alarmRecord.lastTripTimestamp).format(dateFormat) : 'Never'),
                      m('td.bb.bl-0.pr2.truncate', m(m.route.Link, { href: `/point/${alarmRecord['@rid'].slice(1)}?pointhistorytab=Incidents`, class: 'truncate' }, [
                        alarmRecord.alarmName || 'Unnamed Alarm'
                      ])),
                      (function () {
                        var statusText = alarmRecord.alarmStatus ? alarmRecord.alarmStatus : 'Clear';
                        var statusClass = ['Alarm Tripped', 'Pending Alarm Reset'].find(a => a == alarmRecord.alarmStatus) ? 'red' : ['Pending Alarm Trip'].find(a => a == alarmRecord.alarmStatus) ? '.yellow' : '';
                        return m(`td.bb.bl-0.pr2.truncate.  ${statusClass}`, [
                          m('span', statusText),
                          alarmState.hasAlarmError(alarmRecord) ? m('span.ml2.yellow', '⚠️ Error') : null
                        ]);
                      })(),
                      m('td.bb.bl-0.pr2.truncate' + (parseInt(alarmRecord.acknowledged) && !alarmRecord.acknowledgedTimestamp ? '.tc' : ''),
                        parseInt(alarmRecord.acknowledged) ?
                          alarmRecord.acknowledgedTimestamp ? moment(alarmRecord.acknowledgedTimestamp).format(dateFormat) : m('i.f4.ion-android-checkbox-outline.white')
                          :
                          ''),
                      m('td.bb.bl-0.pr2.truncate', parseInt(alarmRecord.acknowledged) ?
                        alarmRecord.acknowledgedBy && alarmRecord.acknowledgedBy.firstName && alarmRecord.acknowledgedBy.lastName ?
                          `${alarmRecord.acknowledgedBy.firstName} ${alarmRecord.acknowledgedBy.lastName}`
                          :
                          `Admin`
                        :
                        ``),
                      m('td.bb.bl-0.pr2.truncate.tc', [
                        m('div.flex.items-center.justify-center', [
                          m('i.ion-android-chat.blue.pointer', {
                            onclick: () => commentModal.editDialogue.call(commentModal, latestIncident, { submitCallback: function () {
                              ctrl.init();
                            } }) 
                          }, ` ${commentsCount}`),
                        ])
                      ]),
                      
                    ])
                  } else {
                    return m('tr', { key: `alarm-empty-${index}` }, [
                      m('td.bb.bl-0', m('label', { style: { 'visibility': 'hidden' } }, 'No Data')),
                      m('td.bb.bl-0'),
                      m('td.bb.bl-0'),
                      m('td.bb.bl-0'),
                      m('td.bb.bl-0'),
                      m('td.bb.bl-0'),
                    ])
                  }
                }),
                (() => {
                  if (!ctrl.isLoading() && ctrl.alarmsRids().length == 0 && ctrl.alarmRecordsToShow().length < 10) {
                    let numberFakeRecords = 10 - ctrl.alarmRecordsToShow().length;
                    for (let index = 0; index < numberFakeRecords; index++) {
                      ctrl.alarmRecordsToShow().push({})
                    }
                    m.redraw();
                  }
                })()
              ])
            ]),
            ctrl.alarmsRids().length > 0 ?
              m(alarmsTablePagination, {
                sortByLastActive: ctrl.sortByLastActive(),
                sortByStatus: ctrl.sortByStatus(),
                data: [...ctrl.alarmsRids()],
                updateKey: ctrl.alarmsVersion(),
                onRecordsUpdated: ctrl.handleRecordsUpdated,
                onUserInteraction: ctrl.pauseUpdates,
                recordsToShow: ctrl.alarmRecordsToShow,
                url: `/api/v1/point/alarmdata`,
                location: ctrl.location
              })
              : ''
          ])
        ])
      ])
    ])

  }
};

module.exports = alarmsTable;
